import { useMemo } from 'react';
import {
  useRecentLocations as useSharedRecentLocations,
  type RecentLocationsStorage,
} from '@parleyride/hooks';
import { isDemoSession } from '@/lib/demo/session';
import { DEMO_RECENT_LOCATIONS_PREFIX } from '@/lib/demo/constants';

const webStorageAdapter: RecentLocationsStorage = {
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => localStorage.setItem(key, value),
  removeItem: (key) => localStorage.removeItem(key),
};

/**
 * The demo's own recents, in its own storage.
 *
 * Everything else a demo session touches goes through the fetch/realtime/
 * storage seams, but recent locations were plain `localStorage` — so a visitor
 * with a real account saw their own saved addresses offered inside Maya's
 * demo, and the places they picked while pretending to be Maya were written
 * back over their real ones. sessionStorage under a distinct key gives the
 * demo the same lifetime as the rest of its state: the tab it was opened in.
 */
const demoStorageAdapter: RecentLocationsStorage = {
  getItem: (key) => {
    try {
      return globalThis.sessionStorage?.getItem(DEMO_RECENT_LOCATIONS_PREFIX + key) ?? null;
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      globalThis.sessionStorage?.setItem(DEMO_RECENT_LOCATIONS_PREFIX + key, value);
    } catch {
      /* private mode: recents just do not persist */
    }
  },
  removeItem: (key) => {
    try {
      globalThis.sessionStorage?.removeItem(DEMO_RECENT_LOCATIONS_PREFIX + key);
    } catch {
      /* nothing to do */
    }
  },
};

export function useRecentLocations(maxItems: number = 5) {
  // Read at render: entering or leaving the demo swaps the AuthProvider, which
  // remounts the tree, so this is re-evaluated at exactly the right moment.
  const demo = isDemoSession();
  const storage = useMemo(() => (demo ? demoStorageAdapter : webStorageAdapter), [demo]);
  return useSharedRecentLocations(storage, maxItems);
}
